import { Paragraph } from '@dnb/eufemia';
import { isObject } from '@portals/shared-frontend/utils';
import Prism from 'prismjs';
import { useEffect } from 'react';

interface Props {
  example: unknown;
  contentType: string;
}

function normalizeContentType(contentType: string): string {
  return contentType.split(';')[0]!.trim().toLowerCase();
}

export function isJsonContentType(contentType: string): boolean {
  const normalizedContentType = normalizeContentType(contentType);
  return normalizedContentType === 'application/json' || /^application\/[\w.-]+\+json$/.test(normalizedContentType);
}

/**
 * Add support for content types as needed/requested.
 */
function getPrismLanguage(contentType: string): string {
  const normalizedContentType = normalizeContentType(contentType);

  if (isJsonContentType(normalizedContentType)) {
    return 'json';
  }

  switch (normalizedContentType) {
    case 'application/xml': {
      return 'xml';
    }
    case 'application/x-yaml': {
      return 'yaml';
    }
    default: {
      return 'txt';
    }
  }
}

export default function Example({ example, contentType }: Props) {
  useEffect(() => {
    Prism.highlightAll();
  }, [example, contentType]);

  if (!example) {
    return (
      <Paragraph top="small">
        <i>Example not provided.</i>
      </Paragraph>
    );
  }

  const prismLanguage = getPrismLanguage(contentType);
  const exampleData =
    prismLanguage === 'json' && (isObject(example) || Array.isArray(example))
      ? JSON.stringify(example, null, '\t')
      : example;

  return (
    <pre className="dnb-pre">
      <code className={`language-${prismLanguage}`}>{String(exampleData)}</code>
    </pre>
  );
}
